import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import groupBy from 'lodash/groupBy';
import keys from 'lodash/keys';
import { v4 } from 'uuid';
import { Button, Label, Segment } from 'semantic-ui-react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';

import Loader from 'components/common/Loader';
import ChatAvatar from '../ChatAvatar';

import FileMessage from '../FileMessage';
import OutogingMessage from '../OutgoingMessage';
import SimpleIncomingMessage from '../SimpleIncomingMessage';
import BlocksIncomingMessage from '../BlocksIncomingMessage';
import MessageThumbnail from '../MessageThumbnail';
import LogMessage from '../LogMessage';
import SenderName from '../SenderName';
import languageMap from 'Language/Language';
import './ConversationsStream.scss';
import TranslateIcon from '../../../../../../../../assets/svg/TranslateIcon';
import ResponseSuggestions from '../ResponseSuggestions';
import LabeledCheckbox from '../../../../../../../../components/common/LabeledCheckbox';
import { getCookie, setCookie } from '../../../../../../../../utils/cookiesHelper';
import { PERMISSION_MANAGE_BOT } from '../../../../../../../SettingsMenu/AllPermissions';
const SHOW_LOGS_COOKIE = 'wisevoice_show_logs_';

const RTL_LANGS = ['he'];
const ConversationsStream = ({
  messages,
  canLoadMore,
  isLoadingMore,
  loadMore,
  messagesElement,
  onScrollMessages,
  language,
  currentUser,
}) => {
  const { permissions, user_id } = currentUser;
  const bottomElement = useRef();
  const [showTranslated, setShowTranslated] = useState({});
  const { id: projectId } = useParams();
  const [showLogs, setShowLogs] = useState(
    getCookie(SHOW_LOGS_COOKIE + projectId + '_' + user_id) === 'true' || false
  );
  const hasDevPermission = permissions[projectId].includes(PERMISSION_MANAGE_BOT);

  // let isRtl = RTL_LANGS.includes(language);
  useEffect(() => {
    if (bottomElement) {
      setTimeout(() => {
        bottomElement.current?.scrollIntoView();
      }, 200);
    }
  }, [bottomElement, messages]);

  const groupedMessages = useMemo(() => {
    return groupBy(messages, (message) => new Date(message.timestamp).toDateString());
  }, [messages]);

  const getTimestamp = (timestamp) => {
    return format(new Date(timestamp), 'HH:mm');
  };

  const translationToggle = (messageId) => {
    const newTranslated = { ...showTranslated };
    if (messageId in showTranslated) {
      delete newTranslated[messageId];
    } else {
      newTranslated[messageId] = true;
    }
    setShowTranslated(newTranslated);
  };

  const renderMessages = useCallback(
    (messages) =>
      messages.map((msg, ix) => {
        let { timestamp, id, message_type, message_subtype, sender_id, message_sender } = msg;
        const senderChange = () => {
          return (
            msg?.first_name !== messages[ix - 1]?.first_name ||
            messages[ix - 1]?.message_type === 'log' ||
            messages[ix - 1]?.message_type === 'audit'
          );
        };

        const displayTimestamp = (timestamp, messageType, originalMessage, messageUuid) => {
          if (
            msg?.first_name !== messages[ix + 1]?.first_name ||
            msg?.sender_id !== messages[ix + 1]?.sender_id ||
            messages[ix + 1]?.message_type === 'log' ||
            messages[ix + 1]?.message_type === 'audit'
          ) {
            if (messageType === 'incoming') {
              return (
                <div className="timestamp small-text">
                  {getTimestamp(timestamp)}
                  {Boolean(originalMessage && messageUuid) && (
                    <>
                      <div style={{ marginLeft: 5 }}>•</div>
                      <TranslateIcon className="translate-icon" />
                      <div
                        className="translate-text translate-text-end"
                        onClick={() => translationToggle(messageUuid)}
                      >
                        {messageUuid in showTranslated
                          ? languageMap.seeTranslation
                          : languageMap.seeOriginal}
                      </div>
                    </>
                  )}
                </div>
              );
            } else if (messageType === 'outgoing') {
              return (
                <div className="timestamp timestamp-end small-text">
                  {getTimestamp(timestamp)}
                  {Boolean(originalMessage && messageUuid) && (
                    <>
                      <div style={{ marginLeft: 5 }}>•</div>
                      <TranslateIcon className="translate-icon" />
                      <div
                        className="translate-text translate-text-end"
                        onClick={() => translationToggle(messageUuid)}
                      >
                        {messageUuid in showTranslated
                          ? languageMap.seeTranslation
                          : languageMap.seeOriginal}
                      </div>
                    </>
                  )}
                </div>
              );
            }
          }
        };

        if (message_subtype === 'email transcript') {
          return null;
        }

        if (message_type === 'log') {
          let {
            message: { log_message },
          } = msg;
          if (hasDevPermission && showLogs) {
            return (
              <LogMessage key={id} timestamp={timestamp} messageType={message_type}>
                {log_message}
              </LogMessage>
            );
          } else {
            return null;
          }
        }
        if (message_type === 'audit') {
          let {
            message: { audit_message },
          } = msg;
          return (
            <LogMessage key={id} timestamp={timestamp} messageType={message_type}>
              {audit_message}
            </LogMessage>
          );
        }

        let {
          message: {
            parsedMessage: { blocks },
            parsedMessage,
            is_message_from_user,
            file,
            fileType,
            uuid,
            reply_suggestions,
          },
          original_message: originalMessage,
          sendStatus,
        } = msg;

        if (originalMessage && uuid in showTranslated) {
          parsedMessage = originalMessage.message;
          blocks = originalMessage.message.blocks;
        }

        if (is_message_from_user) {
          if (parsedMessage === 0) {
            const newMessage = {
              id: v4(),
              type: 'incoming',
              content: parsedMessage,
              meta: { ...parsedMessage },
              alternativeText: `${parsedMessage}`,
            };

            return (
              <MessageThumbnail key={id} timestamp={timestamp} position="right center">
                {senderChange() && (
                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: '22px' }}>
                    <ChatAvatar msgSender={message_sender} />
                    <SenderName senderID={sender_id} incoming={true} />
                  </div>
                )}
                <SimpleIncomingMessage key={id} message={newMessage} senderID={sender_id} />
                <FileMessage fileUrl={file} fileType={fileType} senderID={sender_id} />
                {displayTimestamp(timestamp, newMessage.type, originalMessage, uuid)}
              </MessageThumbnail>
            );
          } else if (blocks === undefined) {
            const newMessage = {
              id: v4(),
              type: 'incoming',
              content: parsedMessage,
              meta: { ...parsedMessage },
              alternativeText: `Message not available`,
            };

            return (
              <MessageThumbnail key={id} timestamp={timestamp} position="right center">
                {senderChange() && (
                  <div className="SenderContainer">
                    <ChatAvatar msgSender={message_sender} />
                    <SenderName senderID={sender_id} incoming={true} />
                  </div>
                )}
                <SimpleIncomingMessage key={id} message={newMessage} senderID={sender_id} />
                <FileMessage fileUrl={file} fileType={fileType} senderID={sender_id} />
                {displayTimestamp(timestamp, newMessage.type, originalMessage, uuid)}
              </MessageThumbnail>
            );
          } else {
            return blocks?.map((block, index) => {
              const { text, titleLabel, alternative_text, carousel_elements = [] } = block;
              const newMessage = {
                id: v4(),
                type: 'incoming',
                content: text || titleLabel || carousel_elements[0]?.text,
                meta: { ...block },
                alternativeText: alternative_text,
              };

              return (
                <MessageThumbnail key={index} timestamp={timestamp} position="right center">
                  {index === 0 && senderChange() && (
                    <div className="SenderContainer">
                      <ChatAvatar msgSender={message_sender} />
                      <SenderName senderID={sender_id} />
                    </div>
                  )}
                  <BlocksIncomingMessage
                    key={`msg_${index}`}
                    message={newMessage}
                    senderID={sender_id}
                  />
                  <FileMessage fileUrl={file} fileType={fileType} senderID={sender_id} />
                  {index === blocks.length - 1 &&
                    displayTimestamp(timestamp, newMessage.type, originalMessage, uuid)}
                </MessageThumbnail>
              );
            });
          }
        } else {
          let suggestionsView = undefined;
          if (reply_suggestions?.length > 0) {
            suggestionsView = (
              <ResponseSuggestions suggestions={reply_suggestions} themeColor={'blue'} />
            );
          }
          return blocks?.map((block, index) => {
            const { linkText, text, titleLabel, alternative_text, carousel_elements = [] } = block;

            const newMessage = {
              id: v4(),
              type: 'outgoing',
              content: linkText || text || titleLabel || carousel_elements[0]?.text,
              meta: { ...block },
              alternativeText: alternative_text,
              reply_suggestions,
            };

            return (
              <MessageThumbnail key={index} timestamp={timestamp} position="left center">
                {index === 0 && (
                  <SenderName
                    style={{ display: 'flex', justifyContent: 'flex-end' }}
                    senderID={sender_id}
                  />
                )}
                <OutogingMessage
                  pending={sendStatus === 'pending' || sendStatus === 'failed'}
                  key={`msg_${index}`}
                  message={newMessage}
                  senderID={sender_id}
                />
                <FileMessage
                  key={`file_${index}`}
                  fileUrl={file}
                  fileType={fileType}
                  senderID={sender_id}
                />
                {sendStatus === 'pending' && (
                  <div className="PendingMessage regular-text">{languageMap.pendingSend}</div>
                )}
                {sendStatus === 'failed' && (
                  <div className="PendingMessage regular-text">{languageMap.failSend}</div>
                )}

                {index === blocks.length - 1 && suggestionsView}
                {index === blocks.length - 1 &&
                  displayTimestamp(timestamp, newMessage.type, originalMessage, uuid)}
              </MessageThumbnail>
            );
          });
        }
      }),
    [showTranslated, showLogs]
  );

  const renderConversationStream = () => {
    const today = new Date().toDateString();

    return (
      <div className="Messages" ref={messagesElement}>
        {canLoadMore && (
          <div className="LoadMore">
            {!isLoadingMore && <Button onClick={loadMore}>Load more</Button>}
            {isLoadingMore && <Loader />}
          </div>
        )}
        {keys(groupedMessages).map((date) => {
          return (
            <React.Fragment key={date}>
              <Segment className="DateDivider" textAlign="center" basic>
                <Label className="date-divider-label small-text">
                  {today === date ? 'Today' : date}
                </Label>
              </Segment>
              {renderMessages(groupedMessages[date])}
            </React.Fragment>
          );
        })}
      </div>
    );
  };
  let className = 'ConversationStream';
  if (RTL_LANGS.includes(language)) {
    className += ' ConversationStreamRtl';
  }
  // className += ' ConversationStreamRtl';

  return (
    <div id="conversation-stream" className={className} onScroll={onScrollMessages}>
      {hasDevPermission && (
        <LabeledCheckbox
          containerStyle={{ position: 'absolute', right: 10, top: 12 }}
          label={showLogs ? 'Logs enabled' : 'Logs disabled'}
          checked={showLogs}
          onChange={() => {
            console.log(showLogs);
            setShowLogs(!showLogs);
            setCookie(SHOW_LOGS_COOKIE + projectId + '_' + user_id, !showLogs);
          }}
        />
      )}
      {renderConversationStream()}
      <div className="_bottomElement" ref={bottomElement} />
    </div>
  );
};

export default ConversationsStream;
